import {
  BadgeDollarSign,
  Banknote,
  BookOpenCheck,
  CalendarClock,
  CreditCard,
  FileText,
  GitBranch,
  Percent,
  ReceiptText,
  ShieldCheck,
  ShoppingBag,
} from 'lucide-react';
import NotFoundPage from 'pages/NotFoundPage';
import { isCommerceW3PreviewEnabled } from 'lib/commerceW3Config';
import { COMMERCE_W3_READINESS_SECTIONS } from 'lib/commerceW3Model';

const SECTION_ICONS = {
  catalog: ShoppingBag,
  adjustments: Percent,
  orders: ReceiptText,
  stripe: CreditCard,
  paypal: BadgeDollarSign,
  swift: Banknote,
  billing: FileText,
  webinars: BookOpenCheck,
  consultations: CalendarClock,
  cutover: GitBranch,
};

function ReadinessCard({ section }) {
  const Icon = SECTION_ICONS[section.id] || ShieldCheck;
  return (
    <article className="border border-white/10 bg-white/[0.025] p-5 shadow-[0_20px_65px_rgba(0,0,0,0.25)]">
      <div className="flex items-start justify-between gap-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center border border-gold/25 bg-gold/[0.07] text-gold">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="border border-gold/25 bg-gold/[0.06] px-2.5 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.16em] text-gold">
          Held
        </span>
      </div>
      <h2 className="mt-5 font-display text-xl font-semibold text-white">{section.title}</h2>
      <p className="mt-3 font-body text-sm leading-6 text-white/58">{section.description}</p>
      <div className="mt-5 border-t border-white/10 pt-4">
        <p className="font-body text-[10px] uppercase tracking-[0.18em] text-white/35">Required owner</p>
        <p className="mt-1 font-body text-sm text-white/68">{section.owner}</p>
        <p className="mt-2 font-body text-xs leading-5 text-gold/72">{section.gateIds.join(' · ')}</p>
      </div>
    </article>
  );
}

function CommerceW3Preview() {
  return (
    <main className="min-h-screen bg-obsidian px-6 pb-20 pt-32 text-white">
      <section className="mx-auto max-w-7xl">
        <div className="border border-gold/20 bg-obsidian-surface p-7 shadow-[0_30px_100px_rgba(0,0,0,0.45)] md:p-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 border border-gold/30 bg-gold/5 px-3 py-2 font-body text-xs uppercase tracking-[0.22em] text-gold">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              W3 private Commerce preview
            </div>
            <h1 className="mt-6 font-display text-4xl font-semibold leading-tight md:text-5xl">
              Payment begins with an authoritative order—and access begins only after verified fulfillment.
            </h1>
            <p className="mt-5 font-body text-base leading-7 text-white/62">
              This isolated page records the future Website Commerce boundary. It contains no real products, prices, coupons, orders, provider buttons, bank instructions, invoices, customer records, booking slots, or purchased access.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {COMMERCE_W3_READINESS_SECTIONS.map((section) => (
              <ReadinessCard key={section.id} section={section} />
            ))}
          </div>

          <div className="mt-8 grid gap-4 border-t border-white/10 pt-7 md:grid-cols-3">
            <div>
              <p className="font-body text-[10px] uppercase tracking-[0.18em] text-white/35">Current primary</p>
              <p className="mt-2 font-body text-sm text-white/72">No connected business backend</p>
            </div>
            <div>
              <p className="font-body text-[10px] uppercase tracking-[0.18em] text-white/35">Target rails</p>
              <p className="mt-2 font-body text-sm text-white/72">Stripe and SWIFT; PayPal retained for compatibility</p>
            </div>
            <div>
              <p className="font-body text-[10px] uppercase tracking-[0.18em] text-white/35">Activation</p>
              <p className="mt-2 font-body text-sm text-white/72">No W3 route switch or provider execution</p>
            </div>
          </div>

          <p className="mt-8 border-l-2 border-gold/50 pl-4 font-body text-xs leading-5 text-white/45">
            The direct TMS connection is pending. Payments and business actions remain unavailable until accepted contracts, reconciliation and exact lane approval exist.
          </p>
        </div>
      </section>
    </main>
  );
}

export default function CommerceW3PreviewPage() {
  if (!isCommerceW3PreviewEnabled()) return <NotFoundPage />;
  return <CommerceW3Preview />;
}
